import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, BarChart3, Shield, Zap, Brain, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import SeoMeta from './SeoMeta';

const features = [
  {
    icon: <TrendingUp className="w-5 h-5" />,
    title: 'Data-Driven Research',
    summary: 'Evidence over instinct — AI synthesises millions of data points into one clear research brief.',
    detail: `Most retail investors make decisions based on news headlines, tips from friends, or gut feeling. The problem: by the time a tip reaches you, the smart money has already moved. IndianStockMarketGPT synthesises price history, trading volume, promoter shareholding trends, FII/DII activity, sector rotation signals, and fundamental ratios into a single research summary — the same data points institutional desks pay lakhs a month to access. Every query analyses over 1,000 data indicators. You get the output in plain English, in under 90 seconds, for free. No Bloomberg terminal required.`,
  },
  {
    icon: <Brain className="w-5 h-5" />,
    title: 'AI Stock Research',
    summary: 'Plain-English explanations of RSI, MACD, volume anomalies, and promoter trends — for education.',
    detail: `Technical analysis is powerful but intimidating. RSI divergences, MACD crossovers, Bollinger Band squeezes — these patterns carry real information about market sentiment, but most retail investors either ignore them or misread them. Our AI explains what each signal means in plain English, without jargon. When promoter holding drops two quarters in a row while FIIs accumulate, the AI explains the historical context of what that pattern has meant for similar stocks. This is educational context, not a prediction — but it gives you the vocabulary and framework to ask better questions and understand what you are looking at.`,
  },
  {
    icon: <Zap className="w-5 h-5" />,
    title: 'Real-Time Market Context',
    summary: 'Live NSE/BSE data + AI context in under 90 seconds, any time of day.',
    detail: `Markets move fast. A result announcement, an RBI policy change, or a global macro event can shift a stock's trajectory within minutes. IndianStockMarketGPT connects to live NSE and BSE data feeds so your research starts from current prices, not yesterday's close. Intraday charts, 52-week highs and lows, volume vs 30-day average, P/E relative to sector — all pulled in real time and fed into the AI analysis. Whether you are checking a stock before market open or evaluating a midday breakout, you get context grounded in what is happening right now, not last week.`,
  },
  {
    icon: <BarChart3 className="w-5 h-5" />,
    title: 'Pattern Recognition',
    summary: 'Candlestick formations, volume spikes, options flow — explained historically, in plain English.',
    detail: `Chart patterns have been studied for over a century because price action encodes crowd psychology. A double top at resistance, a bullish engulfing on high volume, a rising wedge approaching a breakout — these formations repeat because human behaviour repeats. Our AI identifies these patterns in NSE and BSE stocks and explains them in plain English: what the pattern is, how it has historically resolved in similar market conditions in Indian markets, and what additional signals (volume confirmation, sector momentum) would strengthen or weaken the case. This is educational context, not a forecast — but it helps you understand the language the market is speaking.`,
  },
  {
    icon: <BookOpen className="w-5 h-5" />,
    title: 'Learn as You Research',
    summary: 'Every research brief is also a finance lesson — understand P/E, EMA, support levels as you go.',
    detail: `Most financial education happens in the abstract — textbooks, YouTube videos, courses. IndianStockMarketGPT flips the model: you learn finance concepts in context, applied to real stocks you actually care about. Ask about Reliance and you learn what EV/EBITDA means in the context of a conglomerate. Ask about HDFC Bank and you understand why NIM (Net Interest Margin) matters more than P/E for banking stocks. Ask about a midcap IT company and you learn how to read quarterly revenue guidance. Every query is a lesson without feeling like homework. Over time, your financial vocabulary and analytical framework develop naturally — one question at a time.`,
  },
  {
    icon: <Shield className="w-5 h-5" />,
    title: 'Institutional Depth, Retail Access',
    summary: 'Research quality that used to cost lakhs per month — now free for every Indian retail investor.',
    detail: `A sell-side research desk at a large brokerage employs teams of analysts, subscribes to expensive data terminals, and produces research notes that retail investors never see — or see only after the institutional clients have already acted. IndianStockMarketGPT democratises this process. The AI has access to the same publicly available data that professional analysts use: SEC/BSE filings, NSE bulk deal data, quarterly results, FII ownership trends, sector comparisons. The difference is that it synthesises and explains this data in plain English for a retail investor who has ₹12,000 in Groww and wants to understand what they own — not a portfolio manager managing ₹500 crore.`,
  },
];

const faqs = [
  {
    q: 'Does the AI give buy or sell recommendations?',
    a: 'No. IndianStockMarketGPT generates educational research briefs — historical facts, data context, and pattern explanations. It never tells you to buy, sell, or hold any stock. All analysis is for informational purposes only. Investment decisions are yours alone.',
  },
  {
    q: 'What data sources does the AI use?',
    a: 'The AI draws on live NSE/BSE price data, historical fundamentals (P/E, EPS, revenue, debt ratios), FII/DII activity data, promoter shareholding filings, sector indices, and technical indicators (RSI, MACD, Bollinger Bands, EMA). All data is from public sources — no proprietary feeds.',
  },
  {
    q: 'Is this SEBI registered? Is it investment advice?',
    a: 'IndianStockMarketGPT is not a SEBI-registered research analyst or investment adviser. All content is educational and informational only. It does not constitute investment advice, a research report under SEBI regulations, or a recommendation to buy or sell any security. Always consult a SEBI-registered investment adviser before making financial decisions.',
  },
  {
    q: 'How is this different from just Googling a stock?',
    a: 'Google search returns news articles, broker pages, and financial portals — each covering a different angle, in different formats, requiring you to synthesise manually. IndianStockMarketGPT asks a single AI to pull all relevant angles — technicals, fundamentals, recent events, sector context — into one coherent research brief, in plain English, in under 90 seconds. It is the difference between reading 15 tabs and reading one well-structured summary.',
  },
];

const comparisonRows = [
  { aspect: 'Time to research one stock', manual: '45–90 minutes', ai: 'Under 90 seconds' },
  { aspect: 'Technical analysis knowledge required', manual: 'High', ai: 'None' },
  { aspect: 'Fundamentals + technicals together', manual: 'Rarely combined', ai: 'Always combined' },
  { aspect: 'Plain English explanations', manual: 'No', ai: 'Yes' },
  { aspect: 'Cost', manual: '₹0 but time-heavy', ai: 'Free' },
  { aspect: 'SEBI-registered advice', manual: 'Depends on source', ai: 'No — educational only' },
];

const FAQItem: React.FC<{ q: string; a: string }> = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full text-left flex items-start justify-between gap-4 px-6 py-5 bg-slate-50 hover:bg-slate-100 transition-colors"
        aria-expanded={open}
      >
        <span className="font-semibold text-slate-800 text-sm leading-snug">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-navy-500 shrink-0 mt-0.5" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />}
      </button>
      {open && (
        <div className="px-6 py-5 bg-white text-slate-600 text-sm leading-relaxed border-t border-slate-100">
          {a}
        </div>
      )}
    </div>
  );
};

const FeaturesGPT = () => (
  <div className="min-h-screen bg-white text-slate-900">
    <SeoMeta
      title="Features — AI Stock Research for Indian Investors | IndianStockMarketGPT"
      description="Discover how IndianStockMarketGPT uses AI to deliver institutional-quality stock research for Indian retail investors. Live NSE/BSE data, technical analysis, fundamentals — free."
      keywords="AI stock research India, NSE stock analysis features, free stock research tool India, technical analysis AI, fundamental analysis AI India"
      url="https://indianstockmarketgpt.com/features"
      type="website"
      siteName="IndianStockMarketGPT"
    />

    {/* Hero */}
    <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-slate-50 border-b border-slate-100">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-xs text-slate-500 tracking-[0.25em] uppercase mb-6">Why 25,000+ investors choose IndianStockMarketGPT</p>
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-serif text-slate-900 leading-[1.1] mb-4">
          The AI research edge
          <br className="hidden md:block" />
          {' '}every investor deserves
        </h1>
        <p className="text-base text-slate-600 leading-relaxed max-w-xl mx-auto mb-6">
          Professional-grade stock research used to require a Bloomberg terminal and a finance team.
          Now it takes a single question — and 90 seconds.
        </p>
        <p className="text-xs text-slate-500 bg-white border border-slate-200 rounded-lg px-4 py-3 max-w-lg mx-auto leading-relaxed">
          <strong className="text-slate-700">Educational tool only.</strong> All analysis is historical and informational. Not investment advice. Not SEBI registered. Always consult a SEBI-registered adviser before making financial decisions.
        </p>
      </div>
    </section>

    {/* Features — detailed list */}
    <section className="pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto divide-y divide-slate-100">
        {features.map(({ icon, title, summary, detail }) => (
          <div key={title} className="py-10">
            <div className="flex gap-5">
              <div className="text-navy-500 shrink-0 mt-1">{icon}</div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-1">{title}</h2>
                <p className="text-sm text-navy-500 font-medium mb-3">{summary}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{detail}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>

    {/* Comparison table */}
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50 border-y border-slate-100">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-xl md:text-2xl font-bold font-serif text-slate-900 mb-2 text-center">
          Manual research vs. AI research
        </h2>
        <p className="text-sm text-slate-500 text-center mb-8">The same stocks. Very different time investment.</p>
        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100">
                <th className="text-left px-5 py-3 text-slate-600 font-medium">What you are researching</th>
                <th className="text-left px-5 py-3 text-slate-600 font-medium">Manual (Google + Screener)</th>
                <th className="text-left px-5 py-3 text-navy-600 font-medium">IndianStockMarketGPT</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {comparisonRows.map(({ aspect, manual, ai }) => (
                <tr key={aspect} className="bg-white hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3.5 text-slate-700">{aspect}</td>
                  <td className="px-5 py-3.5 text-slate-400">{manual}</td>
                  <td className="px-5 py-3.5 text-green-600 font-medium">{ai}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 text-center mt-4">
          IndianStockMarketGPT does not replace a SEBI-registered adviser. It helps you arrive at conversations with your adviser better informed.
        </p>
      </div>
    </section>

    {/* FAQ */}
    <section className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-xl md:text-2xl font-bold font-serif text-slate-900 mb-2 text-center">
          Frequently asked questions
        </h2>
        <p className="text-sm text-slate-500 text-center mb-8">About how the AI works and what it can (and cannot) do.</p>
        <div className="space-y-3">
          {faqs.map(({ q, a }) => <FAQItem key={q} q={q} a={a} />)}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-100 bg-slate-50">
      <div className="max-w-2xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-bold font-serif text-slate-900 mb-4">
          Ready to research smarter?
        </h2>
        <p className="text-sm text-slate-600 mb-8 max-w-md mx-auto leading-relaxed">
          Join 25,000+ Indian investors who use AI to cut through market noise and build their own informed view.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="https://chatgpt.com/g/g-jkxrUFNja-indian-stock-market"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Try Indian Stock Market GPT free"
            className="bg-navy-500 hover:bg-navy-600 text-white font-semibold px-7 py-3.5 rounded-lg transition-all inline-flex items-center gap-2 text-sm justify-center"
          >
            Try IndianStockMarketGPT free
            <ArrowRight className="w-4 h-4" />
          </a>
          <Link
            to="/"
            className="border border-slate-200 text-slate-600 hover:text-navy-500 hover:border-navy-300 px-7 py-3.5 rounded-lg text-sm font-medium transition-all"
          >
            Back to home
          </Link>
        </div>
      </div>
    </section>

    {/* Disclaimer */}
    <section className="py-6 px-4 sm:px-6 lg:px-8 border-t border-slate-100">
      <div className="max-w-3xl mx-auto">
        <p className="text-xs text-slate-400 text-center leading-relaxed">
          For educational purposes only. Not investment advice. Not SEBI registered.
          All analysis is historical and informational — it does not constitute a recommendation to buy, sell, or hold any security.
          Always consult a SEBI-registered investment adviser before making financial decisions.
        </p>
      </div>
    </section>

  </div>
);

export default FeaturesGPT;
